import { lazy, type ComponentType } from "react"

import { logger } from "./logger"

/**
 * `lazy()` for route components, with recovery from a stale deploy.
 *
 * Every route is code-split, and Vite gives each chunk a content hash
 * (`StudentsPage-sZnfz6JY.js`). A deploy replaces the whole nginx image, so
 * the previous build's files stop existing. A tab that was open across the
 * deploy still holds the old module graph and asks for the old filename —
 * which 404s. The import promise rejects, and because `<Suspense>` handles
 * waiting but not failing, React unmounts the tree and the professor gets a
 * blank page.
 *
 * That is not hypothetical: on 2026-08-08 the frontend shipped five times,
 * and navigating to /salones/:id/alumnos from a pre-deploy tab 404'd on
 * StudentsPage, EditStudentModal and two lucide icon chunks, then went white.
 *
 * The recovery is a one-shot reload: the document itself is not hashed, so
 * reloading fetches a fresh index.html pointing at chunk names that exist,
 * on the same URL the professor was already navigating to. It is guarded by
 * a timestamp in sessionStorage so a genuinely broken deploy — or an offline
 * device — degrades to the error boundary instead of a reload loop.
 */

const RELOAD_MARK_KEY = "platonica:chunk-reload-at"

// Long enough that a hard-down asset host can't drive a loop, short enough
// that a professor who hits this again an hour later still gets the silent
// recovery rather than an error screen.
const RELOAD_COOLDOWN_MS = 30_000

/**
 * Did this browser already try the reload trick very recently?
 *
 * sessionStorage throws in Safari private mode and when cookies are blocked.
 * A browser that can't remember is treated as "already reloaded" — refusing
 * to reload shows an error screen with a manual button, while reloading
 * blindly could spin forever. Fail toward the boring outcome.
 */
function reloadedRecently(): boolean {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_MARK_KEY)
    if (!raw) return false
    const at = Number(raw)
    return Number.isFinite(at) && Date.now() - at < RELOAD_COOLDOWN_MS
  } catch {
    return true
  }
}

function markReloaded(): boolean {
  try {
    window.sessionStorage.setItem(RELOAD_MARK_KEY, String(Date.now()))
    return true
  } catch {
    return false
  }
}

/**
 * A failed dynamic import, as opposed to a module that threw while running.
 *
 * The message is the only signal browsers agree on, and each words it
 * differently. Matching loosely is right here: a false positive costs one
 * reload, a false negative costs a blank page.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "")
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /unable to preload css/i.test(message) ||
    /dynamically imported module.*(404|not found)/i.test(message)
  )
}

// The `any` mirrors React.lazy's own constraint. Narrowing it to `never` or
// `unknown` makes the returned component unassignable to a JSX element type —
// which is precisely why the upstream typing is written this way.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyRoute<T extends ComponentType<any>>(
  name: string,
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((error: unknown) => {
      if (!isChunkLoadError(error)) throw error

      if (reloadedRecently()) {
        // We already reloaded and the chunk is still missing, so a stale tab
        // is not the story — the assets are genuinely unreachable. Let it
        // reach the boundary, which offers a manual retry.
        logger.error("spa.chunk_load_failed_after_reload", { route: name })
        throw error
      }

      if (!markReloaded()) {
        logger.error("spa.chunk_load_failed_no_session_storage", { route: name })
        throw error
      }

      logger.info("spa.stale_chunk_reload", { route: name })
      window.location.reload()

      // Deliberately never settles. The reload is already tearing the
      // document down; resolving or rejecting here would flash a loader or
      // an error for the frame or two before it does.
      return new Promise<{ default: T }>(() => {})
    }),
  )
}
